import { useCallback } from 'react';
import { api } from '@/api/client';
import { useCachedResource } from '@/api/useCachedResource';

/**
 * Active login sessions for the Settings → Account tab — a read-only "session
 * history / tracking" panel backed by GET /api/v2/auth/sessions (each login
 * records device, IP, and activity timestamps; the endpoint never returns the
 * refresh-token hash). Self-contained: fetches on mount with the shared api
 * client (the app has no React Query), with its own loading/error/empty states.
 */

interface SessionInfo {
  id: string;
  userAgent: string | null;
  ipAddress: string | null;
  createdAt: string;
  lastUsedAt: string;
  expiresAt: string;
}

// DB timestamps are UTC; show the absolute "signed in" date in UTC to match the
// rest of Settings, but relative "last active" is timezone-agnostic (instants).
const DATE_FMT = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
  timeZone: 'UTC',
});

function formatDate(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : DATE_FMT.format(d);
}

function relativeTime(iso: string): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const min = Math.round((Date.now() - then) / 60000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min} min ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr} hr ago`;
  const day = Math.round(hr / 24);
  return day < 30 ? `${day} day${day > 1 ? 's' : ''} ago` : formatDate(iso);
}

/** Best-effort "Chrome on Windows"-style label from a user-agent string.
 *  Order matters: Edge/Opera masquerade as Chrome, Chrome contains Safari. */
function deviceLabel(ua: string | null): string {
  if (!ua) return 'Unknown device';
  const browser = /Edg\//.test(ua)
    ? 'Edge'
    : /OPR\/|Opera/.test(ua)
      ? 'Opera'
      : /Firefox\//.test(ua)
        ? 'Firefox'
        : /Chrome\//.test(ua)
          ? 'Chrome'
          : /Safari\//.test(ua)
            ? 'Safari'
            : 'Browser';
  const os = /Windows NT/.test(ua)
    ? 'Windows'
    : /Mac OS X|Macintosh/.test(ua)
      ? 'macOS'
      : /Android/.test(ua)
        ? 'Android'
        : /iPhone|iPad|iOS/.test(ua)
          ? 'iOS'
          : /Linux/.test(ua)
            ? 'Linux'
            : '';
  return os ? `${browser} on ${os}` : browser;
}

export function AccountSessions() {
  const fetchSessions = useCallback(
    () => api.get<{ sessions: SessionInfo[] }>('/api/v2/auth/sessions').then((r) => r.sessions ?? []),
    [],
  );
  // Cached: loads once and persists across tab switches (no refetch on remount).
  const { data: sessions, loading, error, refetch } = useCachedResource('auth/sessions', fetchSessions);

  return (
    <section className="settings-sessions" aria-label="Active sessions">
      <h3 className="settings-sessions__title">Active sessions</h3>

      {loading && <p className="settings-muted">Loading sessions…</p>}

      {error && (
        <div className="settings-error" role="alert">
          {error}{' '}
          <button type="button" className="settings-sessions__retry" onClick={refetch}>
            Retry
          </button>
        </div>
      )}

      {!loading && !error && sessions && sessions.length === 0 && (
        <p className="settings-muted">No active sessions.</p>
      )}

      {!loading && !error && sessions && sessions.length > 0 && (
        <ul className="settings-sessions__list">
          {sessions.map((s) => (
            <li className="settings-session" key={s.id}>
              <span className="settings-session__icon" aria-hidden="true">
                <svg viewBox="0 0 24 24" width="18" height="18" fill="none">
                  <rect x="3" y="4" width="18" height="12" rx="2" stroke="currentColor" strokeWidth="1.6" />
                  <path d="M8 20h8M12 16v4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
                </svg>
              </span>
              <div className="settings-session__info">
                <div className="settings-session__device">{deviceLabel(s.userAgent)}</div>
                <div className="settings-session__meta">
                  {s.ipAddress && <span>{s.ipAddress}</span>}
                  <span>Last active {relativeTime(s.lastUsedAt)}</span>
                </div>
              </div>
              <div className="settings-session__date">Signed in {formatDate(s.createdAt)}</div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
